import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Save, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { useConfirm } from "./_shared";

/* ---------------- Drivers ---------------- */
export default function DriversTab() {
  const [drivers, setDrivers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", phone: "", email: "", license_number: "", status: "active" });
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = () => api.get("/admin/drivers").then((r) => setDrivers(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!draft.name || !draft.phone) return toast.error("Name and phone required");
    try {
      await api.post("/admin/drivers", draft);
      toast.success("Driver added");
      setDraft({ name: "", phone: "", email: "", license_number: "", status: "active" });
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (d) => {
    const ok = await confirm({
      title: "Remove this driver?",
      message: "They will lose Back Office access and won't receive new dispatch assignments.",
      meta: `${d.name} · ${d.phone}`,
      confirmLabel: "Remove driver",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/drivers/${d.id}`);
      toast.success("Driver removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setCreating((v) => !v)}
          data-testid="bo-add-driver-btn"
          className="btn-burgundy px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5">
          <Plus size={12} /> Add driver
        </button>
      </div>
      {creating && (
        <div className="glass rounded-2xl p-5 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {["name", "phone", "email", "license_number"].map((k) => (
            <label key={k} className="block">
              <div className="label-caps text-white/50 mb-1">{k.replace("_", " ")}</div>
              <input value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                data-testid={`bo-driver-${k}`}
                className="w-full bg-ink-800 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            </label>
          ))}
          <button onClick={submit} data-testid="bo-driver-save"
            className="btn-burgundy col-span-full px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5">
            <Save size={12} /> Save driver
          </button>
        </div>
      )}
      {drivers.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center text-white/60">No drivers yet. Add your first one above.</div>
      ) : (
        <div className="space-y-3" data-testid="bo-drivers-list">
          {drivers.map((d) => (
            <div key={d.id} data-testid={`bo-driver-${d.id}`}
              className="glass rounded-2xl p-4 flex items-center justify-between gap-4">
              <div>
                <div className="font-serif text-lg">{d.name}</div>
                <div className="text-sm text-white/60">{d.phone} · {d.email || "no email"}</div>
                <div className="text-xs text-white/40 mt-0.5">
                  License {d.license_number || "—"} · <span className="text-platinum">{d.status}</span>
                </div>
              </div>
              <button onClick={() => remove(d)} className="text-white/40 hover:text-red-400">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

